import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import toast from 'react-hot-toast'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Link, Tooltip, Typography } from '@mui/material'
import translate from '../../i18n/translate'
import { formatOtpDate } from '../UI/OtpPhonesPage'
import { getApiErrorMessage, RobotSmsPolicyVersion, robotSmsPolicyApi } from '../../modules/services/robotSmsPolicyService'
import UploadRobotSmsPolicyModal from './UploadRobotSmsPolicyModal'
import { formatFileSize, gridSx } from './utils'
import useStyles from './useStyles'

// Upload new PDF versions, choose the active one, disable old ones.
// Exactly one version is always active; versions are never deleted.
const PolicyVersionsTab = () => {
  const intl = useIntl()
  const classes = useStyles()

  const [isUploadOpen, setIsUploadOpen] = useState(false)
  const [versionToActivate, setVersionToActivate] = useState<RobotSmsPolicyVersion | null>(null)

  const { data: versions = [], isFetching } = robotSmsPolicyApi.useGetRobotSmsPolicyVersionsQuery()
  const [activateVersion, { isLoading: isActivating }] = robotSmsPolicyApi.useActivateRobotSmsPolicyVersionMutation()
  const [setVersionDisabled] = robotSmsPolicyApi.useSetRobotSmsPolicyVersionDisabledMutation()

  const activeVersion = versions.find((item) => item.isActive)

  const onActivateConfirm = async () => {
    if (!versionToActivate) return

    try {
      await activateVersion(versionToActivate.id).unwrap()
      toast.success(intl.formatMessage({ id: 'robotSmsPolicyActivateSuccess' }, { version: versionToActivate.version }))
    } catch (error) {
      toast.error(getApiErrorMessage(error) || intl.formatMessage({ id: 'robotSmsPolicyUpdateFailed' }))
    } finally {
      setVersionToActivate(null)
    }
  }

  const onDisabledChange = async (row: RobotSmsPolicyVersion) => {
    try {
      await setVersionDisabled({ id: row.id, isDisabled: !row.isDisabled }).unwrap()
    } catch (error) {
      toast.error(getApiErrorMessage(error) || intl.formatMessage({ id: 'robotSmsPolicyUpdateFailed' }))
    }
  }

  const columns = useMemo<GridColDef<RobotSmsPolicyVersion>[]>(() => [
    {
      field: 'version',
      flex: 0.6,
      sortable: false,
      headerName: intl.formatMessage({ id: 'robotSmsPolicyVersion' }),
      renderCell: ({ row }) => <Typography fontWeight={600}>{row.version}</Typography>,
    },
    {
      field: 'status',
      flex: 0.8,
      sortable: false,
      headerName: intl.formatMessage({ id: 'robotSmsPolicyStatus' }),
      renderCell: ({ row }) => {
        if (row.isActive) return <Chip color="success" size="small" label={translate('robotSmsPolicyStatusActive')} />
        if (row.isDisabled) return <Chip size="small" label={translate('robotSmsPolicyStatusDisabled')} />

        return <Chip variant="outlined" size="small" label={translate('robotSmsPolicyStatusAvailable')} />
      },
    },
    {
      field: 'documentUrl',
      flex: 1.4,
      sortable: false,
      headerName: intl.formatMessage({ id: 'robotSmsPolicyFile' }),
      renderCell: ({ row }) => (
        <Box className={classes.Cell}>
          <Link href={row.documentUrl} target="_blank" rel="noreferrer" color="#fc7f03">
            {translate('robotSmsPolicyOpen')}
          </Link>
          {row.fileName && (
            <Typography variant="body2" color="text.secondary" className={classes.FileName}>
              {row.fileName} {formatFileSize(row.fileSize)}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'createdAt',
      flex: 1,
      sortable: false,
      headerName: intl.formatMessage({ id: 'robotSmsPolicyUploaded' }),
      renderCell: ({ row }) => (
        <Box className={classes.Cell}>
          <Typography>{formatOtpDate(row.createdAt)}</Typography>
          {row.uploadedByPhone && <Typography variant="body2" color="text.secondary">{row.uploadedByPhone}</Typography>}
        </Box>
      ),
    },
    {
      field: 'activatedAt',
      flex: 0.9,
      sortable: false,
      headerName: intl.formatMessage({ id: 'robotSmsPolicyActivatedAt' }),
      renderCell: ({ row }) => formatOtpDate(row.activatedAt),
    },
    {
      field: 'acceptedCount',
      flex: 0.7,
      sortable: false,
      headerName: intl.formatMessage({ id: 'robotSmsPolicyAccepted' }),
    },
    {
      field: 'actions',
      flex: 1.4,
      sortable: false,
      headerName: intl.formatMessage({ id: 'robotSmsPolicyActions' }),
      renderCell: ({ row }) => (
        <Box className={classes.Actions}>
          <Button
            color="warning"
            variant="contained"
            size="small"
            disabled={row.isActive || row.isDisabled}
            onClick={() => setVersionToActivate(row)}
          >
            {translate('robotSmsPolicyMakeActive')}
          </Button>
          {/* One version is always active, so the active one can't be switched off */}
          <Tooltip title={row.isActive ? intl.formatMessage({ id: 'robotSmsPolicyActiveCantDisable' }) : ''}>
            <span>
              <Button color="warning" size="small" disabled={row.isActive} onClick={() => onDisabledChange(row)}>
                {translate(row.isDisabled ? 'robotSmsPolicyEnable' : 'robotSmsPolicyDisable')}
              </Button>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [intl, classes])

  return (
    <>
      <div className={classes.TabToolbar}>
        <Button color="warning" variant="contained" className={classes.AddButton} onClick={() => setIsUploadOpen(true)}>
          {translate('robotSmsPolicyUpload')}
        </Button>
      </div>

      <Box className={classes.Info}>
        <Typography variant="body2" color="text.secondary">
          {translate('robotSmsPolicyHint')}
        </Typography>
        {activeVersion && (
          <Typography fontWeight={600}>
            {intl.formatMessage({ id: 'robotSmsPolicyActiveSummary' }, { version: activeVersion.version, count: activeVersion.acceptedCount })}
          </Typography>
        )}
      </Box>

      <DataGrid
        sx={gridSx}
        rows={versions}
        columns={columns}
        loading={isFetching}
        getRowHeight={() => 'auto'}
        disableSelectionOnClick
        autoHeight
        hideFooter
        density="comfortable"
      />

      {isUploadOpen && (
        <UploadRobotSmsPolicyModal
          existingVersions={versions.map((item) => item.version)}
          lastVersion={versions[0]?.version}
          onClose={() => setIsUploadOpen(false)}
        />
      )}

      <Dialog open={!!versionToActivate} onClose={() => setVersionToActivate(null)}>
        <DialogTitle>{translate('robotSmsPolicyMakeActive')}</DialogTitle>
        <DialogContent>
          <Typography>{intl.formatMessage({ id: 'robotSmsPolicyActivateConfirm' }, { version: versionToActivate?.version })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setVersionToActivate(null)}>
            {translate('cancel')}
          </Button>
          <Button color="warning" variant="contained" onClick={onActivateConfirm} disabled={isActivating}>
            {translate('robotSmsPolicyMakeActive')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default PolicyVersionsTab
